import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getRouteApi, Link } from '@tanstack/react-router';
import { AlignJustifyIcon, LayoutGridIcon, MapPinnedIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import type { ToolbarProps } from './toolbar';

type ViewMode = 'map' | 'grid' | 'list';

type ViewSwitchProps = {
  url: ToolbarProps['url'];
  from: string;
};

const ICON_MAPPING: Record<ViewMode, JSX.Element> = {
  grid: <LayoutGridIcon className="flex items-center" />,
  list: <AlignJustifyIcon className="flex items-center" />,
  map: <MapPinnedIcon className="flex items-center" />,
};

export const ViewSwitch = ({ url, from }: ViewSwitchProps) => {
  const { t } = useTranslation();
  const route = getRouteApi(url);
  const search = route.useSearch();
  const { view } = useMemo(
    () => ({
      view: search.view || 'list',
    }),
    [search]
  );
  const viewOptions = useMemo(
    () => Object.entries(ICON_MAPPING).filter(([key]) => key !== view),
    [view]
  );

  const [isOpen, setIsOpen] = useState(false);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          size="md"
          variant="outline"
          className="flex h-[46px] items-center justify-center gap-[6px] rounded-[6px] border border-[#F1F5F9] bg-white px-6 font-medium text-[#2F3A4A] hover:border-[#07B4C0] hover:bg-[#D9F3F7]"
        >
          {ICON_MAPPING[view as ViewMode]}
          <span className="block max-xl:hidden">{t(view)}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {viewOptions.map(([key, icon]) => (
          <DropdownMenuItem key={`mode_${key}`} className="p-0">
            <Link
              from={from}
              search={(prev) => ({
                ...prev,
                view: key,
              })}
              onClick={() => setIsOpen(false)}
              className="flex w-full items-center justify-center gap-0.5 py-2"
              replace
            >
              {icon} {t(key)}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
